<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = withDefaults(defineProps<{ active: boolean; color?: string }>(), {
  color: '#fff',
})

interface Flake {
  x: number
  y: number
  r: number
  speed: number
  drift: number
  phase: number
  opacity: number
}

const canvas = ref<HTMLCanvasElement | null>(null)
const visible = ref(false)
let ctx: CanvasRenderingContext2D | null = null
let flakes: Flake[] = []
let frame = 0
let lastTime = 0
let width = 0
let height = 0

function flakeCount() {
  return Math.round(Math.min(160, Math.max(50, width / 10)))
}

function createFlake(startAbove: boolean): Flake {
  return {
    x: Math.random() * width,
    y: startAbove ? -Math.random() * height : -10,
    r: 1 + Math.random() * 2.5,
    speed: 30 + Math.random() * 50,
    drift: 10 + Math.random() * 20,
    phase: Math.random() * Math.PI * 2,
    opacity: 0.5 + Math.random() * 0.5,
  }
}

function resize() {
  if (!canvas.value) return
  const dpr = window.devicePixelRatio || 1
  width = window.innerWidth
  height = window.innerHeight
  canvas.value.width = width * dpr
  canvas.value.height = height * dpr
  ctx = canvas.value.getContext('2d')
  ctx?.setTransform(dpr, 0, 0, dpr, 0, 0)
}

function tick(time: number) {
  const dt = Math.min((time - lastTime) / 1000, 0.05)
  lastTime = time
  if (!ctx) return

  ctx.clearRect(0, 0, width, height)
  ctx.fillStyle = props.color
  ctx.shadowColor = 'rgba(0, 0, 0, 0.18)'
  ctx.shadowBlur = 3

  flakes = flakes.filter((flake) => {
    flake.y += flake.speed * dt
    flake.phase += dt
    if (flake.y - flake.r > height) {
      if (!props.active) return false
      Object.assign(flake, createFlake(false))
    }
    const x = flake.x + Math.sin(flake.phase) * flake.drift
    ctx!.globalAlpha = flake.opacity
    ctx!.beginPath()
    ctx!.arc(x, flake.y, flake.r, 0, Math.PI * 2)
    ctx!.fill()
    return true
  })

  if (flakes.length === 0 && !props.active) {
    visible.value = false
    frame = 0
    return
  }
  frame = requestAnimationFrame(tick)
}

function start() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  visible.value = true
  resize()
  const missing = flakeCount() - flakes.length
  for (let i = 0; i < missing; i++) flakes.push(createFlake(true))
  if (!frame) {
    lastTime = performance.now()
    frame = requestAnimationFrame(tick)
  }
}

watch(
  () => props.active,
  (active) => {
    if (active) start()
  },
)

onMounted(() => {
  window.addEventListener('resize', resize)
  if (props.active) start()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', resize)
  if (frame) cancelAnimationFrame(frame)
})
</script>

<template>
  <canvas v-show="visible" ref="canvas" class="snowfall" aria-hidden="true" />
</template>

<style scoped>
.snowfall {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 0;
}
</style>
